import { useCompanySettings } from '@/hooks/useCompanySettings'

export interface CurrencyValueProps {
  value: number | string | null | undefined
  /** Overrides the company default, e.g. when showing a foreign-currency claim. */
  currency?: string
  /** Drops the decimal part — used for large dashboard totals. */
  compact?: boolean
  className?: string
}

/**
 * Renders money using the company's configured currency.
 *
 * Never hardcodes a symbol: the reference design shows Bangladeshi taka, but the same
 * build has to serve an AED or USD entity, and currency is a company setting.
 */
export function CurrencyValue({ value, currency, compact, className }: CurrencyValueProps) {
  const { settings } = useCompanySettings()

  if (value === null || value === undefined || value === '') {
    return <span className={className}>—</span>
  }

  const amount = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(amount)) {
    return <span className={className}>—</span>
  }

  // No settings means the company's currency could not be loaded. A number with no symbol
  // is honest; a taka symbol on a dirham figure is not.
  const code = currency ?? settings?.defaultCurrency
  const locale = document.documentElement.lang || 'en'

  // Intl gives correct digit grouping per locale — Arabic and English group differently.
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? 0 : 2,
  }).format(amount)

  if (!code) {
    return (
      <span
        className={className}
        title="The company's currency could not be loaded, so no symbol is shown."
      >
        {formatted}
      </span>
    )
  }

  const symbol = currency && currency !== settings?.defaultCurrency
    ? code
    : (settings?.currencySymbol ?? code)

  return (
    <span className={className} title={`${code} ${formatted}`}>
      {symbol} {formatted}
    </span>
  )
}
